/**
 * Kompetenzprofil — the contract `competency_profile` v1 (al-tutor
 * `app/crud/competency_profile_ops.py`) and the pure view built from it.
 *
 * Values are LEVELS, never percentages: 0 offen · 1 im Aufbau · 2 sicher ·
 * 3 gefestigt; "bestätigt" (rank 4, a passed assessment) is a ✓ badge, not a
 * fourth ring. An axis shows the lower median of its measured objectives;
 * untouched objectives only count towards coverage.
 */

/** `goals`: the customer's course goals (PRD mastery-objectives M3) — only on courses that have any. */
export type CompetencyLensKey = 'areas' | 'bloom' | 'dqr' | 'goals';
export type CompetencyChart = 'radar' | 'list' | 'matrix';
export type AxisState = 'measured' | 'first_evidence' | 'open' | 'not_captured';
export type ObjectiveStatus = 'strong' | 'building' | 'refresh' | 'open';

export interface CompetencyAxis {
  key: string;
  label: string | null;
  order: number;
  group: string | null;
  state: AxisState;
  level: number | null;
  level_tier: string | null;
  total: number;
  measured: number;
  tiers: Record<string, number>;
  status: Record<ObjectiveStatus, number>;
  due: number;
  misconceptions?: number;
  verified: number;
  indirect: boolean;
  objective_keys: string[];
}

export interface CompetencyLens {
  chart: CompetencyChart;
  axes: CompetencyAxis[];
  notes: { key: string; count: number }[];
}

export interface CompetencyObjective {
  objective_key: string;
  description: string | null;
  chapter_id: string | null;
  chapter_title: string | null;
  chapter_order: number | null;
  bloom_level: string | null;
  dqr_area: string | null;
  rank: number;
  tier: string;
  status: ObjectiveStatus;
  due: boolean;
  answers: number;
  correct: number;
  assisted?: number;
  misconceptions?: number;
  sure?: number;
  sure_correct?: number;
  due_at?: string | null;
  last_at: string | null;
}

export interface CompetencySummary {
  objectives: number;
  measured: number;
  verified: number;
  due: number;
  misconceptions?: number;
  status: Record<ObjectiveStatus, number>;
}

export interface CompetencyBehaviour {
  answers: number;
  correct: number;
  correct_unassisted: number;
  sure: number;
  sure_correct: number;
  calibration_ready: boolean;
  /** Typed answers per SOLO level (PRD mastery-objectives M5); null/absent without any. */
  solo?: Record<'prestructural' | 'unistructural' | 'multistructural' | 'relational' | 'extended_abstract', number> | null;
}

export interface CompetencyCourse {
  course_id: string;
  moodle_course_uuid: string | null;
  course_name: string | null;
  summary: CompetencySummary;
  lenses: Record<'areas' | 'bloom' | 'dqr', CompetencyLens> & { goals?: CompetencyLens };
  objectives: CompetencyObjective[];
  behaviour: CompetencyBehaviour | null;
  retired: { objective_key: string; description: string | null; rank: number }[];
  last_at: string | null;
}

export interface CompetencyProfile {
  version: number;
  generated_at: string;
  audience: 'lecturer' | 'learner';
  model: 'v2' | 'v1';
  scale: { max: number; rings: string[]; verified: string };
  default_course_id: string | null;
  overall: { summary: CompetencySummary; lens: CompetencyLens } | null;
  courses: CompetencyCourse[];
  courses_without_objectives: { course_id: string; course_name: string | null }[];
  behaviour: CompetencyBehaviour | null;
}

/** "Alle Kurse" in the course selection. */
export const OVERALL = '__overall__';
export const LEVEL_TIERS = ['open', 'learning', 'proficient', 'mastered'] as const;
export const STATUS_ORDER: ObjectiveStatus[] = ['strong', 'building', 'refresh', 'open'];
/** Radar between these many captured axes (the backend decides the same). */
export const RADAR_MIN_AXES = 3;
export const RADAR_MAX_AXES = 8;

export type Translate = (key: string, params?: Record<string, unknown>) => string;

export interface ObjectiveRow {
  id: string;
  objective: string;
  level: string;
  bloom: string;
  answers: string;
  review: string;
  last: string;
}

export interface AxisView {
  key: string;
  /** "2 · Ohmsches Gesetz", "Anwenden", "Fertigkeiten" */
  label: string;
  /** A short hint under the label (Bloom verb, DQR description). */
  hint: string | null;
  group: string | null;
  state: AxisState;
  level: number;
  verified: boolean;
  levelLabel: string;
  measuredText: string;
  /** measured / total on the 0..3 scale (the dashed coverage line). */
  coverage: number;
  chips: { tone: 'accept' | 'warning' | 'error' | 'info' | 'neutral'; text: string }[];
  note: string | null;
  rows: ObjectiveRow[];
  courseId: string | null;
}

export interface CompetencyView {
  courseId: string;
  lens: CompetencyLensKey;
  chart: CompetencyChart;
  axes: AxisView[];
  summary: CompetencySummary;
  notes: string[];
  furthest: string[];
  open: string[];
  /** Screen-reader sentence for the radar. */
  aria: string;
  empty: boolean;
}

export function levelKey(level: number | null | undefined): string {
  const l = Math.max(0, Math.min(3, Number(level) || 0));
  return LEVEL_TIERS[l];
}

export function axisTitle(axis: CompetencyAxis, lens: CompetencyLensKey | 'courses', t: Translate): string {
  if (lens === 'bloom') return t(`components.competency.bloom.${axis.key.slice('bloom:'.length)}`);
  if (lens === 'dqr') return t(`components.competency.dqr.${axis.key.slice('dqr:'.length)}`);
  if (lens === 'courses') return axis.label || t('components.competency.unknownCourse');
  if (lens === 'goals') return axis.label || '—';
  const title = axis.label || t('components.competency.otherArea');
  return axis.group === 'other' ? title : `${axis.order} · ${title}`;
}

function axisHint(axis: CompetencyAxis, lens: CompetencyLensKey | 'courses', t: Translate): string | null {
  if (lens === 'bloom') return t(`components.competency.bloomHint.${axis.key.slice('bloom:'.length)}`);
  if (lens === 'dqr') return t(`components.competency.dqrHint.${axis.key.slice('dqr:'.length)}`);
  return null;
}

function date(iso: string | null | undefined, lang: string): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString(lang || 'de');
}

export function objectiveRow(o: CompetencyObjective, t: Translate, lang: string): ObjectiveRow {
  const p = 'components.competency';
  let answers = o.answers ? t(`${p}.answers`, { correct: o.correct || 0, answers: o.answers }) : '—';
  if (o.assisted) answers += ` · ${t(`${p}.assisted`, { count: o.assisted })}`;
  if (o.misconceptions) answers += ` · ${t(`${p}.misconceptions`, { count: o.misconceptions })}`;
  const level = o.rank >= 4
    ? `${t(`${p}.levels.mastered`)} ✓`
    : t(`${p}.levels.${levelKey(o.rank)}`);
  return {
    id: o.objective_key,
    objective: o.description || o.objective_key,
    level: o.status === 'refresh' ? `${level} · ${t(`${p}.status.refresh`)}` : level,
    bloom: o.bloom_level ? t(`${p}.bloom.${o.bloom_level}`) : '—',
    answers,
    review: o.due ? t(`${p}.due`) : date(o.due_at, lang),
    last: date(o.last_at, lang),
  };
}

function chipsFor(axis: CompetencyAxis, t: Translate): AxisView['chips'] {
  const p = 'components.competency';
  const chips: AxisView['chips'] = [];
  if (axis.status?.refresh) chips.push({ tone: 'warning', text: `${axis.status.refresh} ${t(`${p}.status.refresh`)}` });
  if (axis.misconceptions) chips.push({ tone: 'error', text: t(`${p}.misconceptionChip`, { count: axis.misconceptions }) });
  return chips;
}

function axisView(
  axis: CompetencyAxis, lens: CompetencyLensKey | 'courses', byKey: Map<string, CompetencyObjective>,
  t: Translate, lang: string,
): AxisView {
  const p = 'components.competency';
  const level = axis.level ?? 0;
  let levelLabel: string;
  if (axis.state === 'not_captured') levelLabel = t(`${p}.states.not_captured`);
  else levelLabel = t(`${p}.levels.${levelKey(level)}`);
  let note: string | null = null;
  if (axis.state === 'first_evidence') note = t(`${p}.firstEvidence`);
  if (axis.indirect) note = t(`${p}.indirect`);
  if (axis.state === 'not_captured' && lens === 'dqr') note = t(`${p}.dqrNotCaptured`);
  const rows = axis.objective_keys
    .map((k) => byKey.get(k))
    .filter((o): o is CompetencyObjective => !!o)
    .map((o) => objectiveRow(o, t, lang));
  return {
    key: axis.key,
    label: axisTitle(axis, lens, t),
    hint: axisHint(axis, lens, t),
    group: axis.group,
    state: axis.state,
    level,
    verified: axis.verified > 0 && axis.verified >= axis.measured && axis.measured > 0,
    levelLabel,
    measuredText: axis.total ? t(`${p}.measured`, { measured: axis.measured, total: axis.total }) : '',
    coverage: axis.total ? (3 * axis.measured) / axis.total : 0,
    chips: chipsFor(axis, t),
    note,
    rows,
    courseId: lens === 'courses' ? axis.key.slice('course:'.length) : null,
  };
}

/** The view for one course (or OVERALL) and lens. Pure; the component keeps
 *  the result until course, lens or profile change (Regel 18). */
export function buildView(
  profile: CompetencyProfile, courseId: string, lens: CompetencyLensKey, t: Translate, lang: string,
): CompetencyView | null {
  const p = 'components.competency';
  let source: CompetencyLens;
  let summary: CompetencySummary;
  let axisLens: CompetencyLensKey | 'courses' = lens;
  let objectives: CompetencyObjective[] = [];
  if (courseId === OVERALL && profile.overall) {
    source = profile.overall.lens;
    summary = profile.overall.summary;
    axisLens = 'courses';
  } else {
    const course = profile.courses.find((c) => c.course_id === courseId) || profile.courses[0];
    if (!course) return null;
    courseId = course.course_id;
    source = course.lenses[lens] || course.lenses.areas;
    if (!course.lenses[lens]) lens = 'areas';
    summary = course.summary;
    objectives = course.objectives;
  }
  const byKey = new Map(objectives.map((o) => [o.objective_key, o]));
  const axes = source.axes.map((a) => axisView(a, axisLens, byKey, t, lang));
  const notes = (source.notes || []).map((n) => t(`${p}.notes.${n.key}`, { count: n.count }));
  const captured = axes.filter((a) => a.state === 'measured' || a.state === 'first_evidence');
  const top = Math.max(0, ...captured.map((a) => a.level));
  const furthest = top > 0 ? captured.filter((a) => a.level === top).map((a) => a.label) : [];
  const open = axes.filter((a) => a.state === 'open').map((a) => a.label);
  const aria = t(`${p}.chartAria`, {
    lens: t(axisLens === 'courses' ? `${p}.overall` : `${p}.lenses.${lens}`),
    summary: axes.map((a) => `${a.label}: ${a.levelLabel}`).join('; '),
  });
  return {
    courseId, lens, chart: source.chart, axes, summary, notes, furthest, open, aria,
    empty: summary.measured === 0,
  };
}
